import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

const CITIES = ["Santos","Praia Grande","São Vicente","Guarujá","Cubatão","Bertioga","Mongaguá","Itanhaém","Peruíbe"];

export function ProProfileEditor() {
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(false);
  const [servicesText, setServicesText] = useState("");

  useEffect(()=>{
    api.get("/professionals/me").then(r=>{
      setP(r.data);
      setServicesText((r.data.services||[]).join("\n"));
    }).catch(()=>toast.error("Perfil não encontrado. Faça login como profissional."));
  }, []);

  const save = async () => {
    setLoading(true);
    try {
      const payload = {
        name: p.name, profession: p.profession, specialty: p.specialty,
        register_number: p.register_number, description: p.description,
        city: p.city, neighborhood: p.neighborhood, address: p.address,
        phone: p.phone, whatsapp: p.whatsapp, photo_url: p.photo_url,
        services: servicesText.split("\n").map(s=>s.trim()).filter(Boolean),
        benefit_summary: p.benefit_summary, featured: false,
      };
      const { data } = await api.patch("/professionals/me", payload);
      setP(data);
      toast.success("Perfil atualizado! Enviamos para revisão administrativa.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (!p) return <Card className="p-6 gs-shadow">Carregando…</Card>;

  const upd = (k) => (e) => setP({...p, [k]: e.target.value});

  return (
    <Card className="p-6 gs-shadow space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Meu Perfil</div>
        <Badge className="bg-[#E6F4F1] text-[#0D7C66] border-0">{p.status}</Badge>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="text-xs text-slate-500">Nome</label><Input data-testid="pro-edit-name" value={p.name||""} onChange={upd("name")}/></div>
        <div><label className="text-xs text-slate-500">Foto (URL)</label><Input data-testid="pro-edit-photo" value={p.photo_url||""} onChange={upd("photo_url")} placeholder="https://…"/></div>
        <div><label className="text-xs text-slate-500">Profissão</label><Input data-testid="pro-edit-profession" value={p.profession||""} onChange={upd("profession")}/></div>
        <div><label className="text-xs text-slate-500">Especialidade</label><Input data-testid="pro-edit-specialty" value={p.specialty||""} onChange={upd("specialty")}/></div>
        <div><label className="text-xs text-slate-500">Registro (CRM/CRO/CREFITO…)</label><Input data-testid="pro-edit-register" value={p.register_number||""} onChange={upd("register_number")}/></div>
        <div><label className="text-xs text-slate-500">Cidade</label>
          <select data-testid="pro-edit-city" value={p.city||""} onChange={upd("city")} className="w-full h-10 rounded-md border border-input px-3 text-sm">
            <option value="">Selecione</option>
            {CITIES.map(c=><option key={c}>{c}</option>)}
          </select>
        </div>
        <div><label className="text-xs text-slate-500">Bairro</label><Input data-testid="pro-edit-neighborhood" value={p.neighborhood||""} onChange={upd("neighborhood")}/></div>
        <div className="sm:col-span-2"><label className="text-xs text-slate-500">Endereço</label><Input data-testid="pro-edit-address" value={p.address||""} onChange={upd("address")}/></div>
        <div><label className="text-xs text-slate-500">Telefone</label><Input data-testid="pro-edit-phone" value={p.phone||""} onChange={upd("phone")}/></div>
        <div><label className="text-xs text-slate-500">WhatsApp</label><Input data-testid="pro-edit-whatsapp" value={p.whatsapp||""} onChange={upd("whatsapp")}/></div>
      </div>
      <div><label className="text-xs text-slate-500">Descrição</label><Textarea data-testid="pro-edit-description" rows={4} value={p.description||""} onChange={upd("description")}/></div>
      <div><label className="text-xs text-slate-500">Serviços (um por linha)</label><Textarea data-testid="pro-edit-services" rows={4} value={servicesText} onChange={e=>setServicesText(e.target.value)}/></div>
      <div><label className="text-xs text-slate-500">Benefício para associados</label><Input data-testid="pro-edit-benefit" value={p.benefit_summary||""} onChange={upd("benefit_summary")} placeholder="Ex: 15% off em consultas"/></div>
      <div className="flex justify-end">
        <Button data-testid="pro-edit-save" onClick={save} disabled={loading} className="bg-[#0D7C66] hover:bg-[#074E41]">{loading?"Salvando...":"Salvar alterações"}</Button>
      </div>
    </Card>
  );
}

export function AdminProsApproval() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("PENDENTE");

  const load = () => api.get("/admin/professionals",{params:{status_filter: status||undefined}}).then(r=>setItems(r.data));
  useEffect(() => { load(); }, [status]);

  const setPro = async (id, newStatus) => {
    try {
      await api.patch(`/admin/professionals/${id}/status`, { status: newStatus });
      toast.success(`Perfil marcado como ${newStatus}`);
      load();
    } catch (e) { toast.error("Erro ao atualizar"); }
  };

  return (
    <Card className="p-5 gs-shadow" data-testid="admin-pros-approval">
      <div className="flex items-center gap-2 mb-4">
        <label className="text-xs text-slate-500">Filtrar por status</label>
        <select data-testid="admin-pros-filter" value={status} onChange={e=>setStatus(e.target.value)} className="h-9 rounded-md border px-2 text-sm">
          <option value="">Todos</option>
          {["PENDENTE","EM ANÁLISE","APROVADO","PUBLICADO","PENDÊNCIA","SUSPENSO"].map(s=><option key={s}>{s}</option>)}
        </select>
      </div>
      <div className="space-y-2">
        {items.map(p => (
          <div key={p.id} className="flex items-center justify-between border-b border-slate-100 py-3" data-testid={`admin-pro-row-${p.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              {p.photo_url && <img src={p.photo_url} alt="" className="w-10 h-10 rounded-lg object-cover"/>}
              <div className="min-w-0">
                <div className="font-semibold truncate">{p.name}</div>
                <div className="text-xs text-slate-500">{p.profession} • {p.specialty} • {p.city}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-[#E6F4F1] text-[#0D7C66] border-0">{p.status}</Badge>
              <Button size="sm" variant="outline" data-testid={`approve-${p.id}`} onClick={()=>setPro(p.id, "PUBLICADO")}>Publicar</Button>
              <Button size="sm" variant="outline" data-testid={`suspend-${p.id}`} onClick={()=>setPro(p.id, "SUSPENSO")}>Suspender</Button>
            </div>
          </div>
        ))}
        {items.length===0 && <div className="text-sm text-slate-500 text-center py-8">Nenhum profissional neste filtro.</div>}
      </div>
    </Card>
  );
}
